// src/services/images.js
// CRA-safe image URL builder: never touches local /public files or signed URLs.
// Transforms ONLY Supabase public-object URLs using the Supabase render endpoint.

function norm(url = "") {
  if (!url) return "";
  // absolute http(s)
  if (/^https?:\/\//i.test(url)) return url;
  // site-absolute ("/foo.png") or relative ("foo.png")
  return url.startsWith("/") ? url : `/${url}`;
}

function isLocalPublic(u = "") {
  // Anything that starts with a single "/" is a CRA /public asset when built/served by your app.
  // (We treat it as local even in production so it never gets query params.)
  return /^\/(?!\/)/.test(u);
}

function isSigned(u = "") {
  // don't ever add params to signed URLs
  return /[?&](token|signature|X-Amz-Signature|X-Amz-Credential|X-Amz-Security-Token)=/i.test(u);
}

function isSupabasePublic(u = "") {
  // typical public object URL
  return /\/storage\/v1\/object\/public\//.test(u);
}

function toSupabaseRender(u = "") {
  // Convert: /object/public/...  -> /render/image/public/...
  return u.replace("/object/public/", "/render/image/public/");
}

// --- public API ---
export function buildSrc(url, { w = 640, q = 75, fmt = "webp" } = {}) {
  const u = norm(url);
  if (!u) return "";

  // 1) local CRA /public assets -> return as-is (prevents your 403)
  if (isLocalPublic(u)) return u;

  // 2) signed/expiring URLs -> return as-is
  if (isSigned(u)) return u;

  // 3) Supabase public objects -> use Supabase render for lightweight resizing
  if (isSupabasePublic(u)) {
    const base = toSupabaseRender(u);
    const joiner = base.includes("?") ? "&" : "?";
    return `${base}${joiner}width=${w}&quality=${q}&format=${fmt || "webp"}`;
  }

  // 4) Everything else (backend absolute URLs, other CDNs) -> pass through untouched
  return u;
}

// If we can transform (Supabase public), make a real srcset; otherwise just return single URL
export function buildSrcSet(url, widths = [320, 480, 640, 768, 960, 1280, 1536], { q = 75, fmt = "webp" } = {}) {
  const u = norm(url);
  if (!u || isLocalPublic(u) || isSigned(u) || !isSupabasePublic(u)) {
    return buildSrc(url, { q, fmt }); // single URL
  }
  return widths.map(w => `${buildSrc(url, { w, q, fmt })} ${w}w`).join(", ");
}
